import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ElementGroupDefaultRow } from "../../ReviewPage/ElementGroupsPanel/ElementGroupDefaultRow";
import { RouteInspectorV3 } from "../RouteInspectorV3";
import { GroupVisibilityToggleButton } from "../GroupVisibilityToggleButton";
import { findRouteGroupByRouteId } from "../utils/findRouteGroupByRouteId";
import type { AllRoutesGroupRowProps } from "./types";

export const AllRoutesGroupRow = ({
  canEdit,
  totalRouteCount,
  isAllRoutesMode,
  onSelectAllRoutes,
  targetRef,
  selectedGroupLabel,
  selectedGroupRoutes,
  selectedRoute,
  routeGroups,
  routeGroupOptions,
  onSelectRoute,
  onJumpToSelectedRouteGroup,
  onRenameGroup,
  onDeleteRoute,
  onChangeRouteType,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  seenIds,
  isPathEditing,
  isPathEditingDirty,
  isPathEditingSubmitting,
  pathEditingMeters,
  onStartPathEditing,
  onSubmitPathEditing,
  onCancelPathEditing,
  allRouteIds,
  hiddenRouteIds,
  onToggleRoutesVisibility,
  areAllRoutesHidden,
  boqPositions,
  onSaveLvMatch,
}: AllRoutesGroupRowProps) => {
  const { t } = useTranslation();

  const selectedRouteLvMatch = useMemo(() => {
    if (!selectedRoute) return undefined;
    const group = findRouteGroupByRouteId({
      routeGroups,
      routeId: selectedRoute.id,
    });
    return group?.routes.find((route) => route.id === selectedRoute.id)
      ?.lvMatch;
  }, [routeGroups, selectedRoute]);

  const areAllHidden = useMemo(
    () => areAllRoutesHidden(allRouteIds),
    [areAllRoutesHidden, allRouteIds],
  );

  return (
    <ElementGroupDefaultRow
      title={t("smartSurveyor.reviewPage.allRoutes")}
      count={totalRouteCount}
      isSelected={isAllRoutesMode}
      onClick={() => !isAllRoutesMode && onSelectAllRoutes()}
      rowRef={isAllRoutesMode ? targetRef : undefined}
      containerClassName={
        isAllRoutesMode
          ? "!border-2 !border-blue-400 rounded-lg overflow-hidden my-1"
          : undefined
      }
      startAdornment={
        <GroupVisibilityToggleButton
          isHidden={areAllHidden}
          onToggle={() => onToggleRoutesVisibility(allRouteIds)}
          title={
            areAllHidden
              ? t("smartSurveyor.reviewPage.showAllOnPlan")
              : t("smartSurveyor.reviewPage.hideAllOnPlan")
          }
        />
      }
    >
      {isAllRoutesMode && (
        <RouteInspectorV3
          canEdit={canEdit}
          selectedGroupLabel={selectedGroupLabel}
          groupRoutes={selectedGroupRoutes}
          selectedRoute={selectedRoute}
          groupLvMatch={selectedRouteLvMatch}
          routeGroupOptions={routeGroupOptions}
          onSelectRoute={onSelectRoute}
          onJumpToGroup={onJumpToSelectedRouteGroup}
          onRenameGroup={onRenameGroup}
          onDeleteRoute={onDeleteRoute}
          onChangeRouteType={onChangeRouteType}
          isTraversing={isTraversing}
          onStartTraversing={onStartTraversing}
          onStopTraversing={onStopTraversing}
          seenIds={seenIds}
          isAllRoutesMode
          isPathEditing={isPathEditing}
          isPathEditingDirty={isPathEditingDirty}
          isPathEditingSubmitting={isPathEditingSubmitting}
          pathEditingMeters={pathEditingMeters}
          onStartPathEditing={onStartPathEditing}
          onSubmitPathEditing={onSubmitPathEditing}
          onCancelPathEditing={onCancelPathEditing}
          hiddenRouteIds={hiddenRouteIds}
          onToggleRoutesVisibility={onToggleRoutesVisibility}
          boqPositions={boqPositions}
          onSaveLvMatch={onSaveLvMatch}
        />
      )}
    </ElementGroupDefaultRow>
  );
};
